import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import API_URL from '../config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Only show loading state if a token exists and needs to be verified.
  // If there's no token we know immediately the user is unauthenticated.
  const [loading, setLoading] = useState(!!localStorage.getItem('authToken'));
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdminToken, setOriginalAdminToken] = useState(localStorage.getItem('originalAdminToken'));

  // Verify token on mount
  useEffect(() => {
    const controller = new AbortController();

    const verifyToken = async () => {
      const storedToken = localStorage.getItem('authToken');

      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/verify-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${storedToken}`
          },
          signal: controller.signal
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setToken(storedToken);
          // Check if this is an impersonation session
          if (data.user.isImpersonating) {
            setIsImpersonating(true);
          }
        } else {
          // Token invalid, clear it
          localStorage.removeItem('authToken');
          setToken(null);
          setUser(null);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Token verification failed:', error);
        localStorage.removeItem('authToken');
        setToken(null);
        setUser(null);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    verifyToken();
    return () => controller.abort();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token
      localStorage.setItem('authToken', data.token);
      setToken(data.token);
      setUser(data.user);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('originalAdminToken');
      setToken(null);
      setUser(null);
      setIsImpersonating(false);
      setOriginalAdminToken(null);
    }
  };

  const impersonateUser = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/impersonate/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Impersonation failed');
      }

      // Store original admin token for returning later
      localStorage.setItem('originalAdminToken', token);
      setOriginalAdminToken(token);

      // Switch to impersonated user
      localStorage.setItem('authToken', data.token);
      setToken(data.token);
      setUser(data.user);
      setIsImpersonating(true);

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Impersonation error:', error);
      return { success: false, error: error.message };
    }
  };

  const stopImpersonating = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/stop-impersonating`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to stop impersonating');
      }

      // Restore admin session
      localStorage.setItem('authToken', data.token);
      localStorage.removeItem('originalAdminToken');
      setToken(data.token);
      setUser(data.user);
      setIsImpersonating(false);
      setOriginalAdminToken(null);

      return { success: true };
    } catch (error) {
      console.error('Stop impersonation error:', error);
      // Fallback: try to restore from stored admin token
      if (originalAdminToken) {
        localStorage.setItem('authToken', originalAdminToken);
        localStorage.removeItem('originalAdminToken');
        setToken(originalAdminToken);
        setIsImpersonating(false);
        setOriginalAdminToken(null);
        // Re-verify to get user data
        window.location.reload();
      }
      return { success: false, error: error.message };
    }
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const isManager = () => {
    return user && user.role === 'manager';
  };

  const isManagerOrAdmin = () => {
    return user && (user.role === 'admin' || user.role === 'manager');
  };

  const getAuthHeader = useCallback(() => {
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, [token]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAdmin,
    isManager,
    isManagerOrAdmin,
    getAuthHeader,
    isAuthenticated: !!user,
    // Impersonation
    isImpersonating,
    impersonateUser,
    stopImpersonating
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
